import React, { FC, useEffect, useState } from 'react'
import useCountDown from 'react-countdown-hook'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import moment from 'moment'
import { CustomTheme } from 'themes/types'

import { Button, Card, CardContent, CardMedia, StyledEngineProvider, ThemeProvider, Typography, useMediaQuery } from '@mui/material'

import { Spinner } from 'components/core/Progress/Progress-component'
import { useAppSelector } from 'redux/hooks'
import { finishResendConfirmEmail, startPlaygroundMode, startResendConfirmEmail } from 'redux/user/user-actions'
import { IMAGES_PATH, useTheme } from 'themes'

import { COMPONENT_EXTENDER } from 'extender'

import { newFormThemeOverrides, useStyles } from './styles'

const SingleContainerLayout = COMPONENT_EXTENDER('components/layouts/SingleContainerLayout')

const emailConfirmation = IMAGES_PATH('/email-confirm.png')
const successImg = IMAGES_PATH('/success-circle.svg')

const SuccessPage: FC = () => {
    const dispatch = useDispatch()
    const style = useStyles()
    const matches = useMediaQuery((theme: CustomTheme) => theme.breakpoints.up('md'))
    const { formTheme } = useTheme({ formTheme: newFormThemeOverrides })
    const { t } = useTranslation()
    const [showStandard, setShowStandard] = useState(false)
    const [countdownStarted, setCountdownStarted] = useState(false)
    const [timeLeft, { start, reset }] = useCountDown(30000, 1000)

    const { currentUser, success, loader } = useAppSelector(state => state.user)
    const { features } = useAppSelector(state => state.settings)

    useEffect(() => {
        if (success?.RESEND_CONFIRM_EMAIL) {
            setCountdownStarted(true)
            start()
        }
    }, [success?.RESEND_CONFIRM_EMAIL])

    useEffect(() => {
        if (success?.PLAYGROUND_MODE) {
            window.location.href = '/'
        }
    }, [!!success?.PLAYGROUND_MODE])

    useEffect(() => {
        if (countdownStarted && timeLeft <= 0) {
            dispatch(finishResendConfirmEmail())
            reset()
        }
    }, [timeLeft])

    const handlePlaygroundMode = () => {
        dispatch(startPlaygroundMode())
    }

    const handleResendConfirmEmail = () => {
        dispatch(startResendConfirmEmail())
    }

    const emailConfirmationContent = () => (
        <div className={style.root}>
            <Card classes={{ root: style.cardRoot }}>
                {matches && <CardMedia image={emailConfirmation} title={t('Pages.success.EmailConfirmation')} component="img" />}
                <CardContent>
                    <div className={style.successTitle}>
                        <img src={successImg} alt="Success" />
                        <Typography data-sta-id="success-page-h1-success" variant="h1">
                            {t('Pages.success.SuccessAccountCreated')}
                        </Typography>
                    </div>
                    <div className={style.successContent}>
                        <Typography variant="subtitle2">
                            <Trans i18nKey="Pages.success.Subtitle2" values={{ email: currentUser?.email }}>
                                An email has been sent to <strong>{currentUser?.email}</strong>. Please{' '}
                                <strong>{t('Pages.success.ClickOnTheLink')}</strong>
                                to confirm your registration and activate your account.
                            </Trans>
                        </Typography>
                        <Typography variant="subtitle2">{t('Pages.success.Subtitle3')}</Typography>
                    </div>
                    <div className={style.successControls}>
                        <Typography variant="h5">{t('Pages.success.DidntReceiveEmail')}</Typography>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleResendConfirmEmail}
                            disabled={!!success?.RESEND_CONFIRM_EMAIL}
                            data-sta-id="success-page-button-resend-email"
                        >
                            {loader?.RESEND_CONFIRM_EMAIL ? (
                                <Spinner />
                            ) : success?.RESEND_CONFIRM_EMAIL ? (
                                `00:${moment(timeLeft).seconds()}`
                            ) : (
                                t('Pages.success.ClickToResend')
                            )}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    )

    const playgroundModeConfirmation = () => (
        <div className={style.root}>
            <Card classes={{ root: style.cardRoot }}>
                <CardMedia image={emailConfirmation} title={t('Pages.success.EmailConfirmation')} />
                <CardContent>
                    <div className={style.successTitle}>
                        <img src={successImg} alt="Pages.success.Success" />
                        <Typography data-sta-id="success-page-h1-success" variant="h1">
                            {t('Pages.success.SuccessAccountCreated')}
                        </Typography>
                    </div>
                    <div className={style.successContent}>
                        <div className={style.playGroundParagraph}>
                            <Typography variant="subtitle2" className={style.playGroundTypography}>
                                <Trans i18nKey="Pages.success.Subtitle4">
                                    Choose playground user verification mode, if you want to
                                    <strong>verify email, phone number, ID document and Proof of Address automatically.</strong>
                                </Trans>
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                className={style.playGroundButton}
                                onClick={handlePlaygroundMode}
                                data-sta-id="success-page-button-playground-mode"
                                disabled={loader?.PLAYGROUND_MODE}
                            >
                                {loader?.PLAYGROUND_MODE ? <Spinner /> : t('Pages.success.PlaygroundMode')}
                            </Button>
                        </div>
                        <div className={style.playGroundParagraph}>
                            <Typography variant="subtitle2" className={style.playGroundTypography}>
                                <Trans i18nKey="Pages.success.Subtitle5">
                                    Choose standard user verification mode, if you want to
                                    <strong> verify email, phone number, ID document and Proof of Address manually. </strong>
                                </Trans>
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                className={style.playGroundButton}
                                onClick={() => setShowStandard(true)}
                                data-sta-id="success-page-button-standard-mode"
                            >
                                {t('Pages.success.StandardMode')}
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )

    const showRightCard = () => {
        if (features?.PlaygroundEnabled) {
            return showStandard ? emailConfirmationContent() : playgroundModeConfirmation()
        } else {
            return emailConfirmationContent()
        }
    }

    return (
        <SingleContainerLayout successPage>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={formTheme}>{showRightCard()}</ThemeProvider>
            </StyledEngineProvider>
        </SingleContainerLayout>
    )
}

export default SuccessPage
